import type {SuggestObject} from '@yandex-delivery/api/model/suggest'
import {type SuggestValue, Grid, List, Suggest} from '@yandex-delivery/blocks'
import {useController, useFormContext} from '@yandex-delivery/form'
import type {Optional} from '@yandex-delivery/types'
import {useCallback} from 'react'

import {fetchAddressSuggest} from 'src/shared/api'
import {i18n} from 'src/shared/config'
import {OrderEventGoal, useFormUserEventHandler} from 'src/shared/hooks'
import {fromSuggestToPointAddress} from 'src/shared/utils'

import type {Props} from './types'

export function AddressSuggest(props: Props) {
  const {name, helperText, pointType, ...suggestProps} = props
  const {clearErrors, control, getFieldError} = useFormContext()
  const {field} = useController({control, name})
  const error = getFieldError(`${name}.fullName`)
  const handleReachGoal = useFormUserEventHandler()

  const changeHandler = useCallback(
    (suggestAddress: Optional<SuggestObject>, inputValue?: string) => {
      if (suggestAddress) {
        const pointAddress = fromSuggestToPointAddress(suggestAddress)
        field.onChange(pointAddress)
        field.onBlur()

        handleReachGoal(
          pointType === 'a'
            ? OrderEventGoal.AddressFromSuggestResultItemClick
            : OrderEventGoal.AddressToSuggestResultItemClick,
        )

        clearErrors(name)
      } else if (inputValue?.length === 0) {
        field.onChange(undefined)
      }
    },
    [field, handleReachGoal, pointType, clearErrors, name],
  )
  const blurHandler = useCallback(() => {
    // Такой хак нужен для того, чтобы onBlur срабатывал после выбора адреса из списка
    // Так как в этом случае сначала срабатывает onChange, а потом onBlur
    // Список адресов находится вне input и поэтому onBlur срабатывает раньше, чем выбор адреса
    window.setTimeout(() => {
      field.onBlur()
    }, 0)
  }, [field])

  return (
    <Suggest
      defaultText={field.value?.fullName}
      error={!!error}
      fetchSuggest={fetchAddressSuggest}
      helperText={error || helperText}
      name={name}
      onBlur={blurHandler}
      onChange={changeHandler}
      onClick={function clickHandler() {
        handleReachGoal(
          pointType === 'a' ? OrderEventGoal.AddressFromSuggestClick : OrderEventGoal.AddressToSuggestClick,
        )
      }}
      placeholder={i18n('cargo.create.address.street_house', 'Улица, дом')}
      point={field.value?.coordinates}
      pointType={pointType}
      portal={false}
      renderItem={renderItem}
      resetInputOnBlur
      {...suggestProps}
    />
  )
}

function renderItem(point: SuggestValue) {
  return (
    <Grid alignItems="center" justify="space-between" container>
      <Grid item>
        <Grid size="s" container>
          <Grid item xs>
            <List.Label variant="body2">{point.title}</List.Label>
            <List.Description color="minor" variant="caption1">
              {point.subtitle}
            </List.Description>
          </Grid>
        </Grid>
      </Grid>
      <Grid item>
        <List.Description color="minor" variant="caption1">
          {point.distance}
        </List.Description>
      </Grid>
    </Grid>
  )
}
